import type {ReactElement} from 'react';
import React from 'react';
import {EdgeType} from '@PosterWhiteboard/classes/item-effects.class';
import {BaseEdgeEffectsOptions} from '@Panels/poster-editor-item-effects-panel/components/base-edge-effects-options';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromEffectsActiveItem} from '@Hooks/poster-editor/useGetKeyFromEffectsOfActiveItem';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';
import {useGetKeyFromEffectsOfActiveSlideItem} from '@Hooks/poster-editor/useGetKeyFromEffectsOfActiveSlideItem';

interface EdgeEffectsOptionsProps {
  panelId: string;
}

export function EdgeEffectsOptions({...props}: EdgeEffectsOptionsProps): ReactElement | null {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeItemEdgeType = useGetKeyFromEffectsActiveItem('edgeType');
  const activeItemEdgeThickness = useGetKeyFromEffectsActiveItem('edgeThickness');

  const activeSlideUID = useGetSelectedSlideId();
  const slideItemEdgeType = useGetKeyFromEffectsOfActiveSlideItem('edgeType');
  const slideItemEdgeThickness = useGetKeyFromEffectsOfActiveSlideItem('edgeThickness');

  if (!activeItem) {
    return null;
  }

  if (activeItem.isSlideshow() && !activeSlideUID) {
    return null;
  }

  const applyEffectForSlideItem = (valueToApply: EdgeType): void => {
    if (activeItem.isSlideshow() && activeSlideUID && slideItemEdgeType !== undefined && activeSlideUID) {
      if (slideItemEdgeType === valueToApply) {
        return;
      }

      void activeItem.updateSlideFromObject(activeSlideUID, {
        effects: {
          edgeType: valueToApply,
        },
      });
    }
  };

  const applyEffect = (valueToApply: EdgeType): void => {
    if (!activeItem || !('effects' in activeItem)) {
      return;
    }
    void activeItem.updateFromObject({
      effects: {
        edgeType: valueToApply,
      },
      border: {
        solidBorderType: activeItem.effects.getBorderForEdgeEffect(valueToApply),
      },
    });

    if (valueToApply === EdgeType.CURVED || valueToApply === EdgeType.GRADIENT) {
      const thickness = valueToApply === EdgeType.CURVED ? 7 : 4;
      void activeItem.updateFromObject(
        {
          effects: {
            edgeThickness: thickness,
          },
        },
        {undoable: false}
      );
    }
  };

  const onUpdateSlideItemThickness = (edgeThickness: number, undoable = true): void => {
    if (activeItem.isSlideshow() && activeSlideUID && activeSlideUID) {
      void activeItem.updateSlideFromObject(
        activeSlideUID,
        {
          effects: {
            edgeThickness,
          },
        },
        {undoable}
      );
    }
  };

  const onUpdateThickness = (edgeThickness: number, undoable = true): void => {
    void activeItem.updateFromObject(
      {
        effects: {
          edgeThickness,
        },
      },
      {undoable}
    );
  };

  const getOptions = (): ReactElement | null => {
    if (activeItem.isSlideshow() && activeSlideUID && slideItemEdgeType !== undefined && slideItemEdgeThickness !== undefined) {
      return (
        <BaseEdgeEffectsOptions
          panelId={props.panelId}
          selectedEdgeType={slideItemEdgeType}
          onClickEdgeType={applyEffectForSlideItem}
          edgeThickness={slideItemEdgeThickness}
          onUpdateThickness={onUpdateSlideItemThickness}
        />
      );
    }
    if (activeItem && 'effects' in activeItem && activeItemEdgeType !== undefined && activeItemEdgeThickness !== undefined) {
      return (
        <BaseEdgeEffectsOptions
          panelId={props.panelId}
          selectedEdgeType={activeItemEdgeType}
          onClickEdgeType={applyEffect}
          edgeThickness={activeItemEdgeThickness}
          onUpdateThickness={onUpdateThickness}
        />
      );
    }
    return null;
  };

  return getOptions();
}
